/**
 * Toasts (§5.6) and screen-reader announcements (§10 live regions).
 *
 * Toasts auto-dismiss after 4 s (8 s with an action). Danger toasts that report something the
 * player must fix pass `persist: true`. Announcements are throttled to one per 5 s and batched.
 */
import { create } from 'zustand';

export type ToastKind = 'info' | 'success' | 'danger';

export interface ToastAction {
  label: string;
  run(): void;
}

export interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
  action?: ToastAction;
  persist?: boolean;
}

interface ToastOptions {
  action?: ToastAction;
  persist?: boolean;
}

interface ToastState {
  toasts: Toast[];
  push(kind: ToastKind, message: string, options?: ToastOptions): number;
  dismiss(id: number): void;
  clear(): void;
}

const MAX_TOASTS = 3;
let nextId = 1;
const timers = new Map<number, ReturnType<typeof setTimeout>>();

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],
  push(kind, message, options = {}) {
    const id = nextId++;
    // Same message twice in a row: refresh rather than stack.
    const existing = get().toasts.find((t) => t.message === message && t.kind === kind);
    if (existing) get().dismiss(existing.id);
    set((s) => ({ toasts: [...s.toasts, { id, kind, message, ...options }].slice(-MAX_TOASTS) }));
    if (!options.persist) {
      const ms = options.action ? 8000 : 4000;
      timers.set(
        id,
        setTimeout(() => get().dismiss(id), ms),
      );
    }
    return id;
  },
  dismiss(id) {
    const timer = timers.get(id);
    if (timer) clearTimeout(timer);
    timers.delete(id);
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
  },
  clear() {
    for (const timer of timers.values()) clearTimeout(timer);
    timers.clear();
    set({ toasts: [] });
  },
}));

export const toast = {
  info: (message: string, options?: ToastOptions) => useToastStore.getState().push('info', message, options),
  success: (message: string, options?: ToastOptions) =>
    useToastStore.getState().push('success', message, options),
  danger: (message: string, options?: ToastOptions) =>
    useToastStore.getState().push('danger', message, options),
  dismiss: (id: number) => useToastStore.getState().dismiss(id),
};

// ---------------------------------------------------------------------------------------------
// Live announcements
// ---------------------------------------------------------------------------------------------

interface AnnouncerState {
  polite: string;
  assertive: string;
}

export const useAnnouncer = create<AnnouncerState>(() => ({ polite: '', assertive: '' }));

const THROTTLE_MS = 5000;
let queue: string[] = [];
let lastPoliteAt = 0;
/** Messages announced recently (text → time), so a re-mounted screen doesn't repeat itself. */
const recent = new Map<string, number>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

/** End a message with exactly one sentence mark ("Saved" → "Saved.", "Done…" stays). */
function sentence(message: string): string {
  const text = message.trim();
  return /[.!?…:]$/.test(text) ? text : `${text}.`;
}

/**
 * One announcement for a batch of queued messages: duplicates dropped (keeping first-seen order),
 * and, when there is more than one, a count followed by each message ending in a single full stop.
 */
export function composeAnnouncement(messages: readonly string[]): string {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const raw of messages) {
    const text = raw.trim();
    const key = sentence(text);
    if (!text || seen.has(key)) continue;
    seen.add(key);
    unique.push(text);
  }
  if (unique.length <= 1) return unique[0] ?? '';
  return `${unique.length} new updates. ${unique.map(sentence).join(' ')}`;
}

function flush() {
  flushTimer = null;
  if (queue.length === 0) return;
  const text = composeAnnouncement(queue);
  const now = Date.now();
  for (const message of queue) recent.set(message, now);
  queue = [];
  lastPoliteAt = now;
  // Clear first so repeating the same text is announced again.
  useAnnouncer.setState({ polite: '' });
  queueMicrotask(() => useAnnouncer.setState({ polite: text }));
}

/**
 * Announce to screen readers. Polite messages are throttled to one announcement per 5 s
 * (later ones are batched); assertive messages go out immediately.
 */
export function announce(message: string, politeness: 'polite' | 'assertive' = 'polite'): void {
  if (!message) return;
  if (politeness === 'assertive') {
    useAnnouncer.setState({ assertive: '' });
    queueMicrotask(() => useAnnouncer.setState({ assertive: message }));
    return;
  }
  // The same message already waiting, or announced moments ago (e.g. a re-mounted hint), is
  // announced once.
  const said = recent.get(message);
  if (said !== undefined && Date.now() - said < THROTTLE_MS * 2) return;
  if (!queue.includes(message)) queue.push(message);
  if (flushTimer) return;
  const wait = Math.max(0, lastPoliteAt + THROTTLE_MS - Date.now());
  if (wait === 0) flush();
  else flushTimer = setTimeout(flush, wait);
}

/** Test helper: reset throttling state. */
export function resetAnnouncer(): void {
  queue = [];
  recent.clear();
  lastPoliteAt = 0;
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = null;
  useAnnouncer.setState({ polite: '', assertive: '' });
}
